import React, { useState, useEffect, Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { boardService } from "../../../../lib/api/boards";
import { currencyService, Currency } from "../../../../lib/api/currency";
import { useAlert } from "../../../../components/Alert";
import { useBoardStore } from "../../../../stores/boardStore";
import { getWebSocketClient } from "../../../../lib/websocket/boardWebSocket";

export interface CurrencyConversion {
  currency: string;
  rate: number;
  manual: boolean;
  originalRate?: number;
  isLoading?: boolean;
}

interface BoardCurrencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  defaultCurrency: string;
  otherCurrencies: string[];
  conversions: CurrencyConversion[];
  onSave: (
    newDefaultCurrency: string,
    conversions: CurrencyConversion[]
  ) => void;
}

export const BoardCurrencyModal: React.FC<BoardCurrencyModalProps> = ({
  isOpen,
  onClose,
  boardId,
  defaultCurrency,
  otherCurrencies,
  conversions,
  onSave,
}) => {
  const { t } = useTranslation("common");
  const alert = useAlert();
  const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);
  const [conversionRates, setConversionRates] = useState<CurrencyConversion[]>(
    []
  );
  const [isChangingBoardCurrency, setIsChangingBoardCurrency] = useState(false);
  const initialFocusRef = useRef<HTMLSelectElement>(null);
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>([]);

  // Fetch supported currencies
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await currencyService.getCurrencies();
        if (response.success && response.data?.currencies) {
          setAvailableCurrencies(response.data.currencies);
        } else {
          console.warn("Currency API returned unexpected format, using fallback");
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Failed to fetch currencies", error);
        // Fallback to essential currencies if API fails
        setAvailableCurrencies([
          { code: 'USD', symbol: '$', name: 'US Dollar', isMajor: true },
          { code: 'EUR', symbol: '€', name: 'Euro', isMajor: true },
          { code: 'GBP', symbol: '£', name: 'British Pound', isMajor: true },
          { code: 'JPY', symbol: '¥', name: 'Japanese Yen', isMajor: true },
          { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', isMajor: true },
          { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', isMajor: true },
          { code: 'TND', symbol: 'DT', name: 'Tunisian Dinar', isMajor: false },
        ]);
      }
    };
    fetchCurrencies();
  }, []);

  // Sync selectedCurrency with defaultCurrency prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCurrency(defaultCurrency);
    }
  }, [isOpen, defaultCurrency]);

  // Initialize conversion rates from board data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Use the board's current currency data directly - no API call needed on open
    const nonDefaultCurrencies = otherCurrencies.filter(
      (c) => c !== selectedCurrency
    );

    // Initialize rates from board data
    // IMPORTANT: Rates in DB are stored as "1 board_currency = X entity_currency"
    // But we display as "1 entity_currency = X board_currency", so we invert for display
    const initialRates = nonDefaultCurrencies.map((currency) => {
      const existing = conversions.find((c) => c.currency === currency);
      if (existing) {
        return {
          ...existing,
          rate: 1 / existing.rate, // Invert for display
          originalRate: existing.originalRate ? 1 / existing.originalRate : undefined,
        };
      }
      return { currency, rate: 1.0, manual: false, isLoading: false };
    });

    setConversionRates(initialRates);
  }, [isOpen, selectedCurrency, otherCurrencies, conversions]);

  // Get currency info by code
  const getCurrencyInfo = (code: string) => {
    return (
      availableCurrencies.find((c) => c.code === code) || {
        code,
        symbol: code,
        name: code,
      }
    );
  };

  // Handle currency change - use preview API to get new rates
  const handleCurrencyChange = async (newCurrency: string) => {
    setSelectedCurrency(newCurrency);
    setIsChangingBoardCurrency(true);

    try {
      // Get current manual rates to preserve them
      // Need to invert back to DB format (1 board = X entity) before sending
      const manualRates: { [key: string]: number } = {};
      conversionRates.forEach((conv) => {
        if (conv.manual) {
          manualRates[conv.currency] = 1 / conv.rate; // Invert back to DB format
        }
      });

      // Use preview API to get rates for the new currency
      const preview = await boardService.getCurrencyPreview(
        boardId,
        newCurrency,
        manualRates
      );

      // Backend returns { success: true, data: preview }
      // The service already returns response.data.data
      const previewData = preview;
      // Invert rates for display (DB stores as "1 board = X entity", we display as "1 entity = X board")
      setConversionRates(
        previewData.conversions.map((conv) => ({
          ...conv,
          rate: 1 / conv.rate, // Invert for display
          originalRate: conv.originalRate ? 1 / conv.originalRate : undefined,
          isLoading: false,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch currency preview:", error);
      alert.error(t("currency.previewFailed"));
    } finally {
      setIsChangingBoardCurrency(false);
    }
  };

  // Handle conversion rate change (marks as manual)
  const handleRateChange = (currency: string, newRate: string) => {
    const rateValue = parseFloat(newRate);
    if (isNaN(rateValue) || rateValue <= 0) return;

    setConversionRates((prev) =>
      prev.map((conv) =>
        conv.currency === currency
          ? { ...conv, rate: rateValue, manual: true }
          : conv
      )
    );
  };

  // Handle reset rate to automatic - local reset only, no API call
  const handleResetRate = (currency: string) => {
    // Find the current conversion to get the originalRate
    const currentConversion = conversionRates.find(
      (c) => c.currency === currency
    );

    if (currentConversion && currentConversion.originalRate) {
      setConversionRates((prev) =>
        prev.map((conv) =>
          conv.currency === currency
            ? {
              ...conv,
              rate: currentConversion.originalRate as number, // Already inverted for display
              manual: false,
              isLoading: false,
            }
            : conv
        )
      );
    }
  };

  // Handle save with optimistic updates
  const handleSave = async () => {
    // Store original settings for rollback
    const originalSettings = {
      defaultCurrency,
      conversions,
    };

    // Invert rates back to DB format (1 board = X entity) before saving
    const dbFormatConversions = conversionRates.map((conv) => ({
      ...conv,
      rate: 1 / conv.rate, // Invert back to DB format
      originalRate: conv.originalRate ? 1 / conv.originalRate : undefined,
    }));

    // Optimistic update to store
    const newSettings = {
      default: selectedCurrency,
      conversions: dbFormatConversions,
    };

    useBoardStore.getState().setCurrencySettings(newSettings);

    // Close modal immediately for better UX
    onClose();

    // Send to backend
    try {
      await onSave(selectedCurrency, dbFormatConversions);
    } catch (error) {
      // Rollback on error
      useBoardStore.getState().setCurrencySettings({
        default: originalSettings.defaultCurrency,
        conversions: originalSettings.conversions,
      });

      alert.error(t("currency.updateFailed"));
      console.error("Failed to update currency:", error);
    }
  };

  // WebSocket real-time updates
  useEffect(() => {
    if (!isOpen) return;

    try {
      const wsClient = getWebSocketClient();

      const handleCurrencyUpdate = (data: any) => {
        if (data.boardId !== boardId) return;

        // Update only automatic rates, preserve user's unsaved edits
        // Invert rates for display
        setConversionRates((prev) =>
          prev.map((conv) => {
            if (conv.manual) return conv; // Keep manual rates

            const updated = data.currencySettings.conversions.find(
              (c: any) => c.currency === conv.currency
            );

            return updated
              ? {
                ...updated,
                rate: 1 / updated.rate, // Invert for display
                originalRate: updated.originalRate
                  ? 1 / updated.originalRate
                  : undefined,
              }
              : conv;
          })
        );

        // Show notification
        alert.info(t("currency.ratesUpdated"));
      };

      const handleCurrenciesAdded = (data: any) => {
        if (data.boardId !== boardId) return;

        // Add new currencies to the list
        const newCurrencies = data.newCurrencies;
        const newConversions = data.currencySettings.conversions
          .filter((c: any) => newCurrencies.includes(c.currency))
          .map((c: any) => ({
            ...c,
            rate: 1 / c.rate, // Invert for display
            originalRate: c.originalRate ? 1 / c.originalRate : undefined,
          }));

        setConversionRates((prev) => {
          const existingCurrencies = new Set(prev.map((c) => c.currency));
          const toAdd = newConversions.filter(
            (c: any) => !existingCurrencies.has(c.currency)
          );
          return [...prev, ...toAdd];
        });

        // Show notification
        alert.info(
          t("currency.newCurrenciesDetected", {
            currencies: newCurrencies.join(", "),
          })
        );
      };

      wsClient.on("board:currency_updated", handleCurrencyUpdate);
      wsClient.on("board:currencies_added", handleCurrenciesAdded);

      return () => {
        wsClient.off("board:currency_updated", handleCurrencyUpdate);
        wsClient.off("board:currencies_added", handleCurrenciesAdded);
      };
    } catch (error) {
      // WebSocket not initialized, this is okay
      console.log("WebSocket not available for currency updates");
    }
  }, [isOpen, boardId, t, alert]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose} initialFocus={initialFocusRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 modal-backdrop" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col transform transition-all border border-neutral-200 dark:border-neutral-800">
                {/* Header */}
                <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                      {t("currency.boardCurrency")}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                    >
                      <span className="w-5 h-5 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_725495b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_725495b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {/* Default Currency Selection */}
                  <div className="mb-6 relative">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      {t("currency.defaultCurrency")}
                    </label>
                    <select
                      ref={initialFocusRef}
                      value={selectedCurrency}
                      onChange={(e) => handleCurrencyChange(e.target.value)}
                      disabled={isChangingBoardCurrency}
                      className="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    >
                      {availableCurrencies.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                    {/* Loading indicator for currency selector */}
                    {isChangingBoardCurrency && (
                      <div className="absolute right-3 top-9 pointer-events-none">
                        <span className="animate-spin h-5 w-5 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3bedded7.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3bedded7.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                      </div>
                    )}
                  </div>

                  {/* converted currencies journal not changed, simplified for replacement range */}
                  {/* Converted Currencies */}
                  {conversionRates.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                        {t("currency.convertedCurrencies")}
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
                        {t("currency.conversionRate")} (1 {getCurrencyInfo(selectedCurrency).symbol} per entity currency)
                      </p>

                      <div className="space-y-3">
                        {conversionRates.map((conv) => {
                          const currencyInfo = getCurrencyInfo(conv.currency);
                          return (
                            <div
                              key={conv.currency}
                              className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-100 dark:border-neutral-700"
                            >
                              <div className="flex-1">
                                <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                  {currencyInfo.symbol} {currencyInfo.code}
                                </div>
                                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                  {currencyInfo.name}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                  1 {currencyInfo.symbol} =
                                </span>
                                <div className="relative">
                                  <input
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    value={conv.rate}
                                    onChange={(e) =>
                                      handleRateChange(conv.currency, e.target.value)
                                    }
                                    disabled={conv.isLoading || isChangingBoardCurrency}
                                    className="w-32 px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent text-sm disabled:bg-neutral-100 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed opacity-transition bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
                                    style={{
                                      opacity: isChangingBoardCurrency ? 0.5 : 1,
                                    }}
                                  />

                                  {/* Icon overlay */}
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    {conv.isLoading ? (
                                      <span className="animate-spin h-4 w-4 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_ea7697f7.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_ea7697f7.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    ) : conv.manual ? (
                                      <button
                                        onClick={() => handleResetRate(conv.currency)}
                                        className="text-blue-500 hover:text-blue-700 transition-colors p-1 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                                        title={t("currency.resetToAutomatic") as string}
                                      >
                                        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_090b9818.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_090b9818.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                      </button>
                                    ) : (
                                      <span
                                        className="text-amber-500 cursor-help"
                                        title={t("currency.automaticRate") as string}
                                      >
                                        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c230b25c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c230b25c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                  {getCurrencyInfo(selectedCurrency).symbol}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Legal Disclaimer */}
                      <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <p className="text-xs text-amber-800 dark:text-amber-400">
                          {t("currency.disclaimer")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors border border-transparent dark:border-neutral-700"
                  >
                    {t("actions.cancel")}
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary-hover rounded-lg transition-colors shadow-sm"
                  >
                    {t("actions.save")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
